<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import { Motion } from 'motion-v';
import {
    Label,
    SelectContent,
    SelectItem,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectViewport,
} from 'reka-ui';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, onMounted } from 'vue';

const {
    fieldId,
    fieldName,
    label,
    options,
    optionKey,
    disabled = false,
    required = false,
    multiple = false,
    hasEmptyOption = false,
    inlineLabel = false,
    size = 'medium',
    by = null,
    legend = null,
    withoutMargin = false,
} = defineProps({
    fieldId: {
        type: String,
        required: true,
    },
    fieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    options: {
        type: Array,
        required: true,
    },
    optionKey: {
        type: String,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    required: {
        type: Boolean,
        required: false,
    },
    multiple: {
        type: Boolean,
        required: false,
    },
    hasEmptyOption: {
        type: Boolean,
        required: false,
    },
    inlineLabel: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    by: {
        type: String,
        required: false,
    },
    legend: {
        type: String,
        required: false,
    },
    withoutMargin: {
        type: Boolean,
        required: false,
    },
});

const model = defineModel();

const itemClasses = computed(() => [
    'flex cursor-pointer items-center overflow-hidden whitespace-nowrap duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white data-[state=checked]:bg-indigo-600 data-[state=checked]:text-white',
    {
        'h-[40px] px-5 py-2': size === 'medium',
        'h-[28px] px-3 py-1 text-sm': size === 'small',
    },
]);

onMounted(() => {
    if (multiple && model.value === null) {
        model.value = [];
    }
});
</script>

<template>
    <div
        class="flex"
        :class="{
            'mb-4': !withoutMargin,
            'flex-row items-center': inlineLabel,
            'flex-col': !inlineLabel,
        }"
    >
        <Label
            :for="fieldId"
            class="whitespace-nowrap"
            :class="{
                'mr-2': inlineLabel,
                'mb-2': !inlineLabel,
                'text-sm': size === 'small',
            }"
        >
            {{ label }}
            <sup v-if="required" class="text-red-500">*</sup>
        </Label>

        <Field v-model="model" :name="fieldName">
            <div>
                <SelectRoot
                    v-slot="{ open }"
                    v-model="model"
                    :by="by ?? undefined"
                    :disabled="disabled"
                    :multiple="multiple"
                >
                    <SelectTrigger
                        :id="fieldId"
                        class="flex w-full items-center justify-between rounded-sm border border-gray-300 text-left ring-1 duration-150 focus:ring-gray-300 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100"
                        :class="{
                            '!ring-gray-300': open,
                            'ring-transparent': !open,
                            'min-w-[210px] px-5 py-3': size === 'medium',
                            'h-[28px] min-w-[140px] px-3 py-1 text-sm':
                                size === 'small',
                        }"
                    >
                        <template v-if="model">
                            <slot name="buttonContent" />
                        </template>
                        <template v-else> &nbsp;</template>

                        <CaretDownIcon
                            class="ml-2 size-4 shrink-0 text-gray-500 duration-150"
                            :class="{ 'rotate-180': open }"
                        />
                    </SelectTrigger>

                    <SelectPortal>
                        <SelectContent
                            position="popper"
                            :body-lock="false"
                            align="start"
                            :side-offset="4"
                            class="z-50"
                        >
                            <Motion
                                :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                                :animate="{ opacity: 1, scale: 1, y: 0 }"
                                :transition="{ duration: 0.15 }"
                                class="max-h-60 w-(--reka-select-trigger-width) overflow-x-hidden overflow-y-auto rounded-sm border border-gray-300 bg-white shadow-sm"
                            >
                                <SelectViewport>
                                    <SelectItem
                                        v-if="hasEmptyOption && !multiple"
                                        :value="null"
                                        :class="itemClasses"
                                    >
                                        <SelectItemText>&nbsp;</SelectItemText>
                                    </SelectItem>

                                    <SelectItem
                                        v-for="(option, index) in options"
                                        :key="index"
                                        :value="
                                            optionKey
                                                ? option[optionKey]
                                                : option
                                        "
                                        :class="itemClasses"
                                    >
                                        <SelectItemText class="truncate">
                                            <slot
                                                name="optionContent"
                                                :option="option"
                                            />
                                        </SelectItemText>
                                    </SelectItem>
                                </SelectViewport>
                            </Motion>
                        </SelectContent>
                    </SelectPortal>
                </SelectRoot>
            </div>
        </Field>

        <em v-if="legend" class="mt-1 text-xs text-gray-400">
            {{ legend }}
        </em>

        <ErrorMessage :name="fieldName" class="mt-1 text-sm text-red-500" />
    </div>
</template>
